import React, { useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import PropTypes from 'prop-types'
import { createSelector } from 'reselect'
import Episode from './Episode'
import {
  NOT_AVAILABLE,
  STV_COOKIES,
  GUIDANCE_ACCEPTED,
  PIN_CHECK,
  GUIDANCE_TEXT,
  PLAYER_ERROR_CODES_MAP,
  UNKNOWN_ERROR,
  GEOBLOCKED,
} from '../../constants'
import withPlayerApi from '../../hocs/withPlayerApi'
import cookies from '../../utils/cookies'
import {
  setPin,
  fetchLoginEnabled,
  setApiError,
  clearApiError,
  updateEpisodeData,
  fetchNextEpisodeData,
  setCurrentEpisode,
  setHideSponsor,
  setPinAccepted,
} from '../../actions'
import getApiPath from '../../utils/getApiPath'
import setPageName from '../../utils/pageName'
import handleApiError from '../../utils/handleApiError'
import getImageRenditions from '../../utils/getImageRenditions'
import getLoginPortalUrl from '../../utils/getLoginPortalUrl'
import getEpisodeUrl from '../../utils/getEpisodeUrl'
import { getDomain } from '../../utils/browser'
import normalizeEpisodeData from '../../utils/normalizeEpisodeData'

const EpisodeContainer = React.memo((props) => {
  const renderCount = useRef(0)
  renderCount.current += 1

  const {
    dispatch, currentEpisode, nextEpisode, contentType, programme, playerApi,
    accessibility, episodeId,
  } = props
  const initialLoadRef = useRef(true)

  const setPageNameCallback = useCallback(() => {
    setPageName({
      page: contentType === 'shortform' ? 'video' : 'episode',
      guid: programme?.guid,
      episodeGuid: currentEpisode?.episodeInfo?.episodeGuid,
    })
  }, [contentType, programme?.guid, currentEpisode?.episodeInfo?.episodeGuid])

  useEffect(() => {
    setPageNameCallback()
  }, [setPageNameCallback])

  useEffect(() => {
    if (currentEpisode && !nextEpisode) {
      dispatch(fetchNextEpisodeData(currentEpisode.episodeInfo.episodeGuid, props.playerApi))
    }
  }, [dispatch, currentEpisode, nextEpisode, props.playerApi])

  useEffect(() => {
    if (currentEpisode && !initialLoadRef.current) {
      const newUrl = getEpisodeUrl({
        episodeId: currentEpisode.episodeInfo.episodeId,
        programmeGuid: programme.guid,
        path: contentType === 'shortform' ? 'video' : 'episode',
        episodeGuid: currentEpisode.episodeInfo.episodeGuid,
      })

      window.history.pushState({ episodeId: currentEpisode.episodeInfo.episodeId }, '', newUrl)
    } else if (initialLoadRef.current) {
      initialLoadRef.current = false
    }
  }, [currentEpisode, programme.guid, contentType])

  useEffect(() => {
    const handlePopState = () => {
      window.location.reload()
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  useEffect(() => {
    const handleEpisodeChange = async (event) => {
      if (event.data?.type === 'EPISODE_CHANGED' && event.data?.episodeData?.episodeId) {
        const { episodeId: newEpisodeId } = event.data.episodeData

        try {
          const apiPath = getApiPath(contentType)
          const { results } = await props.playerApi({
            path: `/${apiPath}/${newEpisodeId}`,
            dispatch,
            sendGroupToken: true,
          })

          const normalizedData = normalizeEpisodeData({ ...results, episodeId: newEpisodeId })
          dispatch(setCurrentEpisode(normalizedData))
        } catch (error) {
          dispatch(setApiError({
            error: error.message || 'Episode transition failed',
            code: 'EPISODE_CHANGE_ERROR',
          }))
        }
      }
    }

    window.addEventListener('message', handleEpisodeChange)
    return () => {
      window.removeEventListener('message', handleEpisodeChange)
      dispatch(clearApiError())
    }
  }, [
    dispatch,
    currentEpisode?.episodeInfo.episodeGuid,
    contentType,
    props.playerApi,
    props.error?.code,
  ])

  const handlePostcodeSubmitInContainer = async (postcode) => {
    try {
      const groupTokenResponse = await playerApi({
        dispatch,
        path: '/grouptoken',
        query: { postcode, device: 'desktop' },
      })

      if (!groupTokenResponse?.results?.grouptoken) {
        dispatch(setApiError({ error: { code: 'GROUPTOKEN_FETCH_FAILED', message: 'Failed to retrieve group token.' } }))
        return
      }

      const { grouptoken } = groupTokenResponse.results

      const cookieOpts = {
        domain: props.currentDomain, // eslint-disable-line react/prop-types
        expires: 365,
        secure: false,
      }
      cookies.set(STV_COOKIES.STV_UNREGISTERED_GROUPTOKEN, grouptoken, cookieOpts)

      const currentEpisodeId = currentEpisode?.episodeInfo?.episodeId || episodeId
      const pathForEpisodeFetch = `/${getApiPath(contentType)}/${currentEpisodeId}`

      if (!currentEpisodeId) {
        dispatch(setApiError({ error: { code: 'PLAYER_API_PATH_ERROR', message: 'Cannot determine API path for episode.' } }))
        return
      }

      const resultData = await playerApi({
        dispatch,
        path: pathForEpisodeFetch,
        query: { groupToken: grouptoken, accessibility },
      })

      if (!resultData.success) {
        let errorToDispatch
        if (resultData.reason && resultData.reason.code != null) {
          const mappedErrorCode = PLAYER_ERROR_CODES_MAP[resultData.reason.code]
          if (mappedErrorCode) {
            errorToDispatch = {
              ...resultData.reason, // Preserve other properties from original reason
              code: mappedErrorCode,
            }
            if (mappedErrorCode === GEOBLOCKED) {
              errorToDispatch.httpStatus = 403
            }
          } else {
            errorToDispatch = resultData.reason || { code: UNKNOWN_ERROR, message: 'An unknown error occurred.' }
          }
        } else {
          // If no resultData.reason or no resultData.reason.code, use a generic unknown error
          errorToDispatch = { code: UNKNOWN_ERROR, message: 'An unknown error occurred.' }
        }
        dispatch(setApiError({ error: errorToDispatch }))
      } else {
        const normalizedData = normalizeEpisodeData({
          ...resultData.results, episodeId: currentEpisodeId, accessibility,
        })
        dispatch(updateEpisodeData(normalizedData))
        dispatch(clearApiError())
      }
    } catch (submitError) {
      dispatch(setApiError({ error: { code: 'POSTCODE_SUBMIT_EXCEPTION', message: submitError.message || 'Postcode submission failed due to an exception.' } }))
    }
  }

  return <Episode {...props} onPostcodeSubmit={handlePostcodeSubmitInContainer} />
}, (prevProps, nextProps) => {
  const sameEpisode = prevProps.currentEpisode?.episodeInfo.episodeGuid
    === nextProps.currentEpisode?.episodeInfo.episodeGuid
  const sameProgramme = prevProps.programme?.guid
    === nextProps.programme?.guid
  const sameError = prevProps.error === nextProps.error
  const samePinAccepted = prevProps.pinAccepted === nextProps.pinAccepted

  // Only prevent re-render if all relevant props are the same
  return sameEpisode && sameProgramme && sameError && samePinAccepted
})

EpisodeContainer.propTypes = {
  contentType: PropTypes.oneOf(['shortform', 'episode']).isRequired,
  programme: PropTypes.shape({
    guid: PropTypes.string,
  }),
  dispatch: PropTypes.func.isRequired,
  playerApi: PropTypes.func.isRequired,
  currentEpisode: PropTypes.shape({
    episodeInfo: PropTypes.shape({
      episodeId: PropTypes.string,
      episodeGuid: PropTypes.string.isRequired,
    }).isRequired,
  }),
  nextEpisode: PropTypes.shape({
    episodeInfo: PropTypes.shape({
      episodeId: PropTypes.string,
      episodeGuid: PropTypes.string,
    }),
  }),
  error: PropTypes.shape({
    code: PropTypes.string,
    message: PropTypes.string,
  }),
  episodeId: PropTypes.string,
  accessibility: PropTypes.string,
  currentDomain: PropTypes.string,
}

EpisodeContainer.defaultProps = {
  programme: { guid: null },
  currentEpisode: null,
  nextEpisode: null,
  episodeId: null,
  accessibility: '',
  currentDomain: '',
}

// this function determines where the user has been refered from
// a whitelist could be added to config file once we know which 3rd parties will be used
const isFromThirdParty = (refererURL, hostURL) => {
  if ((getDomain(refererURL)).includes(getDomain(hostURL))) {
    return false
  }
  if (refererURL.includes('google.com')) {
    return false
  }
  return true
}

const referedFromThirdParty = (req) => {
  if (req.headers.referer
    && isFromThirdParty(req.headers.referer, req.headers.host)
    && !cookies.get(STV_COOKIES.STV_USERID, req)) {
    return true
  }
  return false
}

export const handleThirdPartyReferral = (req, res) => {
  if (referedFromThirdParty(req)) {
    cookies.set('thirdParty', true)
    res.redirect(`/summary/${req.params.programme}`)
  }
}

EpisodeContainer.getInitialProps = async ({
  query,
  req,
  res,
  reduxStore,
  playerApi,
  asPath,
}) => {
  const validAccessibilityTags = ['ad', 'vs']
  const contentType = asPath.includes('video') ? 'shortform' : 'episode'
  const apiPath = getApiPath(contentType)
  const episodeId = parseInt(query.episodeIdHash, 36)

  let accessibility = ''
  const suffix = query.programme?.split('-').pop()

  if (validAccessibilityTags.includes(suffix)) {
    accessibility = suffix
  }

  const { STV_AUTH_REQUIRED } = STV_COOKIES
  const sendAuthHeader = cookies.get(STV_AUTH_REQUIRED, req) === 'true'
  const fetchEpisode = options => playerApi({
    path: `/${apiPath}/${episodeId}`,
    ...options,
  })

  const {
    STVPLAYER_CONTROLS, STV_GROUPTOKEN, STV_UNREGISTERED_GROUPTOKEN,
  } = STV_COOKIES

  const encodedPin = cookies.get(STVPLAYER_CONTROLS, req)
  if (reduxStore?.dispatch) {
    reduxStore.dispatch(setPin({ encodedPin }))
    await reduxStore.dispatch(fetchLoginEnabled(playerApi))
  }
  const { loginEnabled } = reduxStore?.getState()?.loginStatus

  // Prioritise logged-in token, then unregistered token, then fall back to default.
  let groupToken = cookies.get(STV_GROUPTOKEN, req)
  if (!groupToken || groupToken === 'undefined') {
    groupToken = cookies.get(STV_UNREGISTERED_GROUPTOKEN, req)
  }
  if (!groupToken || groupToken === 'undefined') {
    groupToken = '0010'
  }

  const fetchEpisodeWithToken = token => fetchEpisode({
    dispatch: reduxStore?.dispatch,
    sendGroupToken: true,
    query: {
      groupToken: token,
      accessibility,
    },
    sendAuthHeader,
  })

  let { results, reason } = await fetchEpisodeWithToken(groupToken)
  let error = null
  if (reason) {
    error = handleApiError({ res, apiError: reason })

    if (error.code === 'POSTCODE_REQUIRED') {
      if (reduxStore?.dispatch) {
        reduxStore.dispatch(setApiError({ apiError: reason }))
      }
      // Fallback to a guest token to render the page behind the dialog
      const guestToken = loginEnabled ? '0071' : '0011'
      const guestResponse = await fetchEpisodeWithToken(guestToken)
      results = guestResponse.results
      reason = guestResponse.reason
    }
    // even if you're not logged in, we want to show the page, and let it be indexed
    if (error.code === 'NOT_LOGGED_IN') {
      const response = await fetchEpisodeWithToken('0071')
      results = response.results
      reason = response.reason
    }
  }

  handleThirdPartyReferral(req, res)

  // SEO ~ redirect to home if episode unavailable
  if (error && error.code === 'NOT_AVAILABLE') {
    res.redirect('/')
  }

  if (results && !error && reduxStore?.dispatch) {
    const episodeData = normalizeEpisodeData({ ...results, episodeId, accessibility })
    reduxStore.dispatch(updateEpisodeData(episodeData))
  }

  const errorPage = [404, 500].includes(res?.statusCode)

  const guid = results?.programme?.guid
  // have we accepted guidance for this programme already in this session?
  const prevAccepted = Boolean(guid && cookies.get(guid, req) === GUIDANCE_ACCEPTED)

  const title = reason ? 'Video not available | STV Player' : `STV Player | ${results?.programme?.name} ${results?.title}`
  const description = (contentType === 'shortform') ? results?.summary : results?.programme?.shortDescription
  const summary = results?.summary
  const image = results ? getImageRenditions(results?.images?.[0]?._filepath, ['1024x576'])._renditions['1024x576'] : null
  const fromThirdParty = cookies.get('thirdParty', req)

  if (results?.programme?.premierSportsExclusive) {
    reduxStore?.dispatch(setHideSponsor(results?.programme?.premierSportsExclusive))
  }
  const initialProps = {
    error,
    groupTokenCookie: groupToken,
    contentType,
    episodeId,
    programme: query?.programme,
    layout: errorPage ? 'default' : 'full-width',
    showWallpaper: false,
    title,
    description,
    prevAccepted,
    image,
    summary,
    accessibility,
    fromThirdParty,
    episodeInfo: results
      ? normalizeEpisodeData({ ...results, episodeId, accessibility }).episodeInfo
      : null,
  }
  return initialProps
}

const selectUser = state => state.user
const selectPlayerApiCache = state => state.playerApiCache
const selectEpisode = state => state.episode

const selectPinAcceptance = state => state.pinAcceptance
const selectEncodedPin = state => state.pinAcceptance?.encodedPin
const selectEpisodeData = createSelector(
  [
    selectPlayerApiCache,
    (_, { episodeId, contentType, accessibility }) => ({ episodeId, contentType, accessibility }),
  ],
  (playerApiCache, { episodeId, contentType, accessibility }) => {
    const apiPath = getApiPath(contentType)
    const cacheKey = `/${apiPath}/${episodeId}`
    const cacheEntry = playerApiCache[cacheKey]
    const episodesResults = cacheEntry ? cacheEntry.results : null

    const normalizedData = episodesResults
      ? normalizeEpisodeData({ ...episodesResults, episodeId, accessibility })
      : null

    return normalizedData
  }
)

const mapStateToProps = (state, ownProps) => {
  const { currentlySubscribed } = selectUser(state)
  const { currentEpisode, nextEpisode } = selectEpisode(state)
  const encodedPin = selectEncodedPin(state)
  const pinAcceptanceState = selectPinAcceptance(state)
  const pinAccepted = pinAcceptanceState[ownProps.episodeId] || false
  const episodesData = selectEpisodeData(state, ownProps)

  const guidanceAcceptanceState = state.guidanceAcceptance || {}
  const guidanceAccepted = guidanceAcceptanceState?.all?.guidanceTextAccepted || false
  const requiresGuidance = episodesData?.episodeInfo?.hasGuidanceText || false

  // error from getInitialProps is initialErrorFromProps
  const { error: initialErrorFromProps, currentUrl, currentDomain } = ownProps

  // rawApiErrorFromRedux is state.playerError.apiError
  // (the whole object, e.g. {apiError: actualError} or {apiError: null})
  const rawApiErrorFromRedux = state.playerError?.apiError
  // effectiveApiError is the actual error object or null from within rawApiErrorFromRedux
  // const effectiveApiError = rawApiErrorFromRedux?.apiError // Removed as per no-unused-vars

  let displayError // This will be the final, flat error object or null for UI display

  // 1. First, check for an active API error:
  if (
    rawApiErrorFromRedux
    && rawApiErrorFromRedux.error !== null
    && typeof rawApiErrorFromRedux.error === 'object'
    && Object.keys(rawApiErrorFromRedux.error).length > 0
  ) {
    displayError = rawApiErrorFromRedux.error
  } else if (currentEpisode && currentEpisode.episodeInfo && currentEpisode.episodeInfo.episodeId) {
  // 2. Else, check if episode data has loaded:
    displayError = null // Signifies success and should clear any initial dialog.
  } else {
  // 3. Else (fallback):
    displayError = initialErrorFromProps
  }

  // Original logic for PIN_CHECK and GUIDANCE_TEXT continues,
  // operating on the 'displayError' determined above.
  if (encodedPin && !pinAccepted && requiresGuidance) {
    // PIN is required but not accepted - show PIN Dialog (takes precedence over guidance and login)
    displayError = PIN_CHECK
  } else if (requiresGuidance && !guidanceAccepted) {
    // Check if current displayError (null, initialError, or apiError) is NOT_LOGGED_IN
    const isNotLoggedInError = (typeof displayError === 'object' && displayError?.code === 'NOT_LOGGED_IN')
                             || (typeof displayError === 'string' && displayError === 'NOT_LOGGED_IN')
    if (!isNotLoggedInError) {
      // Guidance is required, not accepted, and not overridden by a NOT_LOGGED_IN error.
      displayError = GUIDANCE_TEXT
    }
  }

  const redirectUrl = `${getLoginPortalUrl(currentDomain)}/?siteAction=remoteLogin&source=${encodeURIComponent(currentUrl)}`

  const result = {
    currentUrl,
    currentDomain,
    currentlySubscribed,
    encodedPin,
    pinAccepted,
    error: displayError,
    apiError: rawApiErrorFromRedux,
    ...episodesData,
    ...(displayError ? { title: getErrorTitle(displayError) } : {}),
    redirectUrl,
    currentEpisode,
    episodeId: ownProps.episodeId,
    accessibility: ownProps.accessibility,
    nextEpisode,
  }

  return result
}

const getErrorTitle = (error) => {
  switch (error.code) {
    case NOT_AVAILABLE: return 'Sorry, that video is no longer available'
    default: return 'Sorry, we couldn\'t find that episode.'
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  dispatch,
  setPinAccepted: () => dispatch(setPinAccepted({ path: ownProps.episodeId })),
})

const enhance = compose(
  withPlayerApi,
  connect(mapStateToProps, mapDispatchToProps)
)

export default enhance(EpisodeContainer)
